import { cn } from "@/lib/cn";

interface Props {
  /** Current on/off state. */
  checked: boolean;
  /** Fired with the next value when the user flips the switch. */
  onCheckedChange: (checked: boolean) => void;
  /** Disables interaction (e.g. while a mutation is pending). */
  disabled?: boolean;
  /** Optional id so an external `<label htmlFor>` can target the control. */
  id?: string;
  /** Accessible label when there is no associated visible `<label>`. */
  "aria-label"?: string;
  /**
   * Points at the field's hint text (see `hintFieldId`), so a screen reader
   * announces WHAT THE SWITCH DOES and not merely its name. Without it a
   * toggle whose explanation lives in a `FieldHint` popover is announced as a
   * bare label — the popover is portalled and mounted only while open, so it
   * can never be the describedby target itself.
   */
  "aria-describedby"?: string;
}

/**
 * Toggle — an accessible styled switch built on a native
 * `<input type="checkbox">` (Sprint 149, TASK-4302). The checkbox itself stays
 * in the DOM (visually hidden via `peer sr-only`) so it keeps native keyboard,
 * focus and form semantics; the track + knob are painted with Tailwind driven
 * by the peer state. No external switch dependency (no Radix Switch).
 *
 * Used for the registry global-flag toggle and the per-tenant override toggles.
 */
export function Toggle({
  checked,
  onCheckedChange,
  disabled = false,
  id,
  "aria-label": ariaLabel,
  "aria-describedby": describedBy,
}: Props) {
  return (
    <label
      className={cn(
        "relative inline-flex h-6 w-11 shrink-0 items-center",
        disabled ? "cursor-not-allowed opacity-50" : "cursor-pointer",
      )}
    >
      <input
        id={id}
        type="checkbox"
        role="switch"
        className="peer sr-only"
        checked={checked}
        disabled={disabled}
        aria-label={ariaLabel}
        aria-describedby={describedBy}
        onChange={(e) => onCheckedChange(e.target.checked)}
      />
      {/* Track */}
      <span
        aria-hidden="true"
        className={cn(
          "h-6 w-11 rounded-full bg-gray-300 transition-colors",
          "peer-checked:bg-brand-primary",
          "peer-focus-visible:ring-2 peer-focus-visible:ring-brand-primary peer-focus-visible:ring-offset-2",
        )}
      />
      {/* Knob */}
      <span
        aria-hidden="true"
        className={cn(
          "absolute left-0.5 h-5 w-5 rounded-full bg-white shadow-sm transition-transform",
          "peer-checked:translate-x-5",
        )}
      />
    </label>
  );
}
